import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  signal,
} from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { ReplaySubject, takeUntil } from 'rxjs';

import { coreSelectors } from 'app/core/state';
import { SocialPageService } from 'app/modules/social/social-page/services/social-page.service';
import { Loggable } from 'app/utils/logging/loggable';
import Social from 'entity/Social';
import User from 'entity/User';

import { ScootaverseRoutingService } from '../services/scootaverse-routing.service';
import { BreadcrumbNavigation } from '../types';

import { ScootaverseEditService } from './../services/scootaverse-edit.service';
import { ScootaversePageService } from './../services/scootaverse-page.service';

@Component({
  selector: 'page-header',
  templateUrl: './page-header.component.html',
  styleUrls: ['./page-header.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PageHeaderComponent extends Loggable implements OnInit, OnDestroy {
  @Input() public roomName?: string;
  @Input() public scootaverseName?: string;
  @Input() public social?: Social;
  @Input() public scootaverseId?: string;

  @Output() public enterRoom = new EventEmitter<string>();

  private destroyed$ = new ReplaySubject<boolean>(1);
  public breadCrumbTree = signal<BreadcrumbNavigation[]>([]);
  private user = signal<User | undefined>(undefined);

  public constructor(
    private router: Router,
    private scootaverseRoutingService: ScootaverseRoutingService,
    private scootaversePageService: ScootaversePageService,
    private scootaverseEditService: ScootaverseEditService,
    private socialPageService: SocialPageService,
    private store: Store,
  ) {
    super();
    this.setDisplayName('PageHeaderComponent');
    this.store
      .select(coreSelectors.getUserEntity)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((user) => this.user.set(user));
  }

  public ngOnInit(): void {
    this.scootaverseRoutingService.pageNavigationState$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((navState) => this.breadCrumbTree.set(navState));
  }

  public getTitle(nav: BreadcrumbNavigation): string {
    let title = '';
    switch (nav.title) {
      case 'scootaverse_name':
        title = this.checkScootaverseName(this.scootaverseName);
        break;
      case 'room_name':
        title = this.checkRoomName(this.roomName);
        break;
      default:
        title = `/ ${nav.title} `;
    }
    return title;
  }

  public routeTo(nav: BreadcrumbNavigation): void {
    switch (nav.title) {
      case 'scootaverse_name':
        this.routeToScootaverse();
        break;
      case 'room_name':
        this.routeToRoom();
        break;
      default:
        return;
    }
  }

  public isEntryFlowActive(): boolean {
    return !!this.breadCrumbTree()?.some((nav) => nav.title.includes('Flow'));
  }

  public async routeToMain(): Promise<void> {
    await this.scootaversePageService.redirectToHome();
  }

  public routeToRoom(): void {
    if (this.social) {
      this.socialPageService.onEnterRoom(this.social).catch((e) => {
        this.error('Error redirecting to room - ', e);
      });
      this.enterRoom.emit(this.social.socialId);
    }
  }

  public routeToScootaverse(): void {
    if (this.scootaverseId) {
      void this.scootaversePageService.onEnterScootaverse(this.scootaverseId);
    }
  }

  public async createScootaverse(): Promise<void> {
    if (!this.user()) {
      void this.router.navigate(['/welcome']);
      return;
    }

    try {
      await this.scootaverseEditService.createScootaverse();
    } catch (error) {
      this.error('Error creating scootaverse - ', error);
    }
  }

  public async createEntryFlow(): Promise<void> {
    if (!this.user()) {
      void this.router.navigate(['/welcome']);
      return;
    }
    await this.scootaversePageService.onCreateEntryFlow(this.scootaverseId);
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  public checkRoomName(roomName?: string): string {
    return roomName?.toUpperCase().endsWith('ROOM') ? `/ ${roomName} ` : `/ ${roomName} Room `;
  }

  public checkScootaverseName(scootaverseName?: string): string {
    return scootaverseName?.toUpperCase().endsWith('ROOM') || scootaverseName?.toUpperCase().endsWith('SCOOTAVERSE')
      ? `/ ${scootaverseName} `
      : `/ ${scootaverseName} Scootaverse `;
  }

  public checkSubNavItem(navItem?: string): string {
    return navItem ? `/ ${navItem}` : ``;
  }
}
